<script setup>

import {ref, computed, onMounted, nextTick} from 'vue';
import StaticTooltip from "@/components/common/other/StaticTooltip";
import Button from "@/components/common/input/Button";
import CheckBox from "@/components/common/input/CheckBox";
import MovingTooltip from "@/components/common/other/MovingTooltip";
import GridFunctionTips from "@/components/common/other/GridFunctionTips";
import GridModeDataNotification from "@/components/shipments/GridModeDataNotification";

const emits = defineEmits(['update:currentPaginationRange', 'update:currentPage', 'click-reset',
  'click-save', 'selected-row', 'update:highlightRows'])

const props = defineProps({

  columns: Array,

  dataCy: String,
  currentPage: {type: Number, default: 1},
  currentPaginationRange: {type: Number},
  pageSize: {type: Number, default: 100},
  allowGridMenu: {type: Boolean, default: false},
  allowPagination: {type: Boolean, default: true},
  selectable: {type: Boolean, default: true},
  rowSelection: {type: Boolean, default: true},
  height: {type: Number, default: 500},
  showDateIndicator: {type: Boolean, default: true},

  dataSource: {
    type: Array,
    default: function () {
      return []
    }
  },

})

const sourceColumnIndex = ref(null);
const openColumnMenuByIndex = ref(null);
const localData = ref([]);
const pageButtons = ref([]);
const paginationSize = ref(10);
const showGridMenu = ref(false);
const closeTimer = ref(null);
const saveTimer = ref(null);
const columnMenuToggleColumns = ref(false);

let previousLeftPos = 0;
let currentSelectedRow = ref();

onMounted(() => {

  localData.value = JSON.parse(JSON.stringify(props.dataSource));

  createPageButtons();

})

const customColumns = computed(() => {

  return props.columns.filter(c => c.enabled === true)
});

const gridHeight = computed(() => {

  return props.height + 'px'
})

const wrapperHeight = computed(() => {

  return props.height + 124 + 'px'
})


const pagedData = computed(() => {

  if (localData.value.length > 0) {

    return localData.value.filter((row, index) => {

      // eg if page size set to 10, and current page is 2 (default)
      // currentPage is a v-model to the chosen state class

      let startIndex = (props.currentPage - 1) * props.pageSize; // 1 X 10 = 10   / start = 10
      let endIndex = props.currentPage * props.pageSize; // 2 X 10 = 20   / end = 20

      // if the current index >= 10 (start), and the index is less then 20 (end), return the array from index 10 to 19  = 10 records inclusive (page size!)
      if (index >= startIndex && index < endIndex) {
        return true
      }

    });
  }
})

function rowDataSource(index) {
  return props.dataSource[index];
}

function generateRowData(row, key, column) {

  if (column) {

    // Get the row data from the past in key from the current column
    if (typeof (row[key]) === 'string') {

      if (row[key]) return row[key]

    }

  }

  return row[key];
}

function handleDragStart(e, column) {

  sourceColumnIndex.value = props.columns.findIndex(i => i.id === column.id);

  if (openColumnMenuByIndex.value !== null) {

    e.preventDefault();
    return;
  }

  if (column.isFixed) {

    e.preventDefault();
    return;
  }

}

function handleDragover(e) {

  e.preventDefault();
  return false;
}

function handleDrop(e, destinationColumnData) {

  e.stopPropagation();

  let mainArrayDestinationIndex = props.columns.findIndex(i => i.id === destinationColumnData.id);

  if (sourceColumnIndex.value !== mainArrayDestinationIndex && !destinationColumnData.isFixed) {

    if (mainArrayDestinationIndex < sourceColumnIndex.value) {

      props.columns.splice(mainArrayDestinationIndex, 0, props.columns[sourceColumnIndex.value]);
      props.columns.splice(sourceColumnIndex.value + 1, 1);

    } else {

      props.columns.splice(mainArrayDestinationIndex + 1, 0, props.columns[sourceColumnIndex.value]);
      props.columns.splice(sourceColumnIndex.value, 1);

    }

    if (props.allowGridMenu) emits('click-save');

  }
}

function setLeftPosition(index) {

  if (index === 0) return 0;

  let firstColumnDatum = parseInt(customColumns.value[0].width) + 11;
  let width = parseInt(customColumns.value[index - 1].width) + 11

  if (index === 1) return firstColumnDatum + 'px';

  if (index === 2) {

    previousLeftPos = width + firstColumnDatum;
    return width + firstColumnDatum + 'px'
  }

  let currentValue = previousLeftPos;

  previousLeftPos = width + previousLeftPos;

  if (customColumns.value[index].isFixed) return width + currentValue + 'px';

}

function setZ(index) {

  if (customColumns.value[index].isFixed) {

    return 99;
  }

}

function setSelectedRow(column, row, index) {

    // if(currentSelectedRow.value === index) return row.selectedRow = !row.selectedRow;

  if(!column.isFixed)
  row.selectedRow = !row.selectedRow;

    // currentSelectedRow.value = index;

    // clearHighlightedRow();

    // row.selectedRow = true;

  // if (props.selectable && !row.checkRow) emits('selected-row', props.dataSource[index], index);

}

function clearHighlightedRow() {

  for (let ea in localData.value) {
    localData.value[ea]['selectedRow'] = false;
  }

}

function getPaginationPages() {
  // Calc the number of pagination buttons required, eg blocks of pages set in paginationSize (10 default)
  return Math.ceil(pageButtons.value.length / paginationSize.value);

}

function splitPagination() {

  return pageButtons.value.filter((row, index) => {

    let startIndex = (props.currentPaginationRange - 1) * paginationSize.value;
    let endIndex = props.currentPaginationRange * paginationSize.value;

    if (index >= startIndex && index < endIndex) {
      return true
    }

  });

}

function createPageButtons() {

  pageButtons.value = [];

  let roundUp = Math.ceil(localData.value.length / props.pageSize);

  for (let i = 0; i < roundUp; i++) {

    pageButtons.value.push(
        {
          number: i + 1,
          notValid: false
        });
  }

}

function pageUp() {

  if (props.currentPaginationRange < getPaginationPages())
    emits('update:currentPaginationRange', props.currentPaginationRange + 1)
}

function pageDown() {

  if (props.currentPaginationRange > 1) emits('update:currentPaginationRange', props.currentPaginationRange - 1)
}

function handleColumnMenu(e, index, column) {

  e.preventDefault();

  if(!column.isFixed)
    openColumnMenuByIndex.value ? openColumnMenuByIndex.value = null :   openColumnMenuByIndex.value = index;
}

function handleMouseEnterColMenu() {

  clearTimeout(saveTimer.value);
  clearTimeout(closeTimer.value);
}

function handleCloseColMenu() {

  closeTimer.value = setTimeout(() => openColumnMenuByIndex.value = null, 1000);

  if(props.allowGridMenu)

    saveTimer.value = setTimeout(() =>  handleColumnWidthSave(), 2000);

}

function handleColumnWidthSave() {

  if(props.allowGridMenu) emits('click-save', true);
}

function dragColumnsMessage(col) {

  if(!col.isFixed) return 'Drag Over Other Colum Headers To Re Order';
  return 'This Column Is Fixed';
}

function resetColumns() {

  props.columns.forEach(function (item) {
    item.enabled = true;
  })
  props.columns.sort((a, b) => {
    return a.id - b.id
  });
}

function handleMenuDisplay() {

  showGridMenu.value = !showGridMenu.value;

}

function toggleColumns() {

  props.columns.forEach(col => {

    if(!col.isFixed && columnMenuToggleColumns.value) col.enabled = true;
    if(!col.isFixed && !columnMenuToggleColumns.value) col.enabled = false;

  })

}

function copyValue(e, text) {

  e.preventDefault();

  try {

    navigator.clipboard.writeText(text);

  } catch (error) {

    console.log('Needs To Be A HTTPS Secure Site To Use Navigator And Clipboard', error)
  }

}

</script>

<template>
  <div class="cmp-sticky-grid_wrapper">
    <div v-if="showGridMenu" class="cmp-sticky-grid_menu">
      <div class="cmp-sticky-grid_menu-title">&#9783 Column Configuration
        <div style="font-size: 10px; margin-left: 20px">Add / Remove Columns</div>
        <div class="cmp-sticky-grid_menu-button-area">
          <Button
              @click="emits('click-reset', $event, resetColumns())"
              type="toolbar"
              title="Restores Original Column Arrangement"
              text="Reset">
          </Button>
          <Button
              @click="emits('click-save', $event)"
              title="Save Column Selections"
              text="Save">
          </Button>
        </div>
      </div>
      <div class="cmp-sticky-grid_menu-checkbox-area">
        <div style="margin: 0px 30px 20px 0; font-weight: bold">
          <CheckBox
              @change="toggleColumns()"
              v-model="columnMenuToggleColumns"
              :label-width="145"
              label="Toggle">
          </CheckBox>
        </div>
        <div v-for="(column, index) in columns">
          <CheckBox
              v-if="column.isRemovable"
              v-model="column.enabled"
              :label-width="145"
              :label="column.title">
          </CheckBox>
        </div>
      </div>
    </div>
    <div class="cmp-sticky-grid_menu-area">
      <StaticTooltip :icon="false">
        <template #content>
          <GridFunctionTips></GridFunctionTips>
        </template>
        <i class="fa-solid fa-circle-info icon-info"></i>
      </StaticTooltip>
      <div class="cmp-sticky-grid_pagination-area">
        <div v-if="props.allowGridMenu" @click="handleMenuDisplay" class="cmp-sticky-grid_menu-icon" title="Column Configuration">&#9776</div>
        <div v-if="getPaginationPages() > 0" class="cmp-sticky-grid_pages-area" title="Total Pages">{{ pageButtons.length }}</div>
        <div v-if="pageButtons.length > 1"> Pages: </div>
        <div v-if="pageButtons.length === 1"> Page: </div>
        <div v-if="getPaginationPages() > 1" style="margin: 20px;">
          <StaticTooltip :icon="false">
            <template #content>
              <span v-if="props.currentPaginationRange > 1">Prev Pages</span>
              <span v-else> No Previous Pages</span>
            </template>
            <Button
                :disabled="props.currentPaginationRange === 1"
                style="width: 20px;"
                @click="pageDown()"
                type="toolbar"
                text="<">
            </Button>
          </StaticTooltip>
          <StaticTooltip :icon="false">
            <template #content>
              <span v-if="props.currentPaginationRange !== getPaginationPages()">Next Pages</span>
              <span v-else> No More Pages</span>
            </template>
            <Button
                :disabled="props.currentPaginationRange === getPaginationPages()"
                style="width: 20px;"
                @click="pageUp()"
                type="toolbar"
                text=">">
            </Button>
          </StaticTooltip>
        </div>
        <div v-for="(button, index) in splitPagination()" :key="index">
          <Button
              @click="$emit('update:currentPage', button.number)"
              :title="`Page ${button.number}`"
              style="width: 30px;"
              :class="`${button.number === props.currentPage && 'cmp-sticky-grid_active-page'}`"
              type="toolbar"
              :text="button.number.toString()">
          </Button>
        </div>
      </div>
      <GridModeDataNotification
          v-if="props.showDateIndicator"
          style="margin-left: auto ">
      </GridModeDataNotification>
    </div>
    <div class="cmp-sticky-grid">
      <table>
        <tr class="cmp-sticky-grid_table-header-row">
          <th
              :title="dragColumnsMessage(col)"
              @dragstart="handleDragStart($event, col)"
              @dragover="handleDragover($event)"
              @drop="handleDrop($event, col)"
              @contextmenu="handleColumnMenu($event, index, col)"
              :style="{minWidth: col.width + 'px', left: setLeftPosition(index), zIndex: setZ(index) }"
              :data-cy="dataCy + 'ColumnIndex' + index"
              :class="{'cmp-sticky-grid_sticky' : col.isFixed}"
              draggable="true" v-for="(col, index) of customColumns">{{ col.title }}
            <div v-if="index === openColumnMenuByIndex"
                 class="cmp-sticky-grid_column-menu" title="Adjust Column Width"
                 @mouseenter="handleMouseEnterColMenu"
                 @mouseleave="handleCloseColMenu">
              <input
                  class="cmp-sticky-grid_column-slider"
                  v-model="col.width"
                  type="range"
                  min="50"
                  max="800"/>
            </div>
<!--            <div style="position: absolute; right: 10px; top: 12px"> &#8645</div>-->
          </th>
        </tr>
        <div v-if="localData.length === 0" style="height: 60px;">
          <div class="cmp-sticky-grid_no-data"><i class="fa-solid fa-database"></i> No Data</div>
        </div>
        <tr v-for="(row, rowIndex) in pagedData" :key="rowIndex" class="cmp-sticky-grid_table-data-row">
          <template v-for="(col, index) of customColumns">
            <td
                v-if="typeof $slots[col.key] !== 'undefined'"
                @click="setSelectedRow(col, row, index)"
                @contextmenu="$event.preventDefault()"
                :style="{maxWidth: col.width + 'px', left: setLeftPosition(index)}"
                :class="{'cmp-sticky-grid_sticky' : col.isFixed,'cmp-sticky-grid_selected' : row.selectedRow}">
              <slot :name="col.key" :column="col" :grid="row" :rowData="rowDataSource(rowIndex)" :rowIndex="rowIndex"></slot>
            </td>
            <td v-else :class="{'cmp-sticky-grid_sticky' : col.isFixed, 'cmp-sticky-grid_selected' : row.selectedRow}"
                @click="setSelectedRow(col, row, index)"
                @contextmenu="copyValue($event, generateRowData(row, col.key, col))"
                :data-cy="dataCy + 'RowIndex' + index"
                :title="generateRowData(row, col.key)"
                :style="{maxWidth: col.width + 'px', left: setLeftPosition(index)}">
              {{ generateRowData(row, col.key, col) }}
            </td>
          </template>
        </tr>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>

table {
  border-spacing: 0;
  font-size: 12px;
}

.cmp-sticky-grid {
  position: relative;
  //width: 100%;
  max-height: v-bind(gridHeight);
  overflow: scroll;
  border-bottom-left-radius: 5px;
  border-left: 1px solid #c8c8c8;

  &_wrapper {
    position: relative;
    height: v-bind(wrapperHeight);
  }

  &_table-header-row th {
    position: sticky;
    top: 0;
    border-bottom: 1px solid #dbdbdb;
    border-right: 1px solid #dbdbdb;
    padding: 5px;
    background: var(--grid-header);
    color: #fcfcfc;
    text-align: left;
    height: 30px;
    font-weight: normal;
    overflow: hidden;
    white-space: nowrap;
  }

  &_table-data-row td {
    border-bottom: 1px solid #dbdbdb;
    border-right: 1px solid #dbdbdb;
    padding-left: 5px;
    height: 28px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &_sticky {
    position: sticky;
    box-shadow: 4px 1px 6px -1px #888888;
    background: #fafafa;
  }

  &_table-data-row:nth-child(even) {
    background: #f5f5f5;
  }

  &_table-data-row:nth-child(odd) {
    background: #ffffff;
  }

  &_selected {
    background-image: linear-gradient(180deg, #fffaed, #f6ebc5);
  }

  &_menu-area {
    display: flex;
    padding: 6px;
    background-image: linear-gradient(90deg, #f5f5f5, #ffffff);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    height: 22px;
    margin-bottom: 5px;
  }

  &_pagination-area {
    display: flex;
    align-items: center;
    font-size: 12px;
  }

  &_pages-area {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 8px;
    height: 22px;
    background-color: #ffffff;
    margin-right: 10px;
    font-size: 12px;
    color: #000000;
    border-radius: 4px;
  }

  &_active-page {
    background-image: none !important;
    background-color: white;
    font-weight: bold;
    color: black !important;
    box-shadow: 1px 1px 2px 0px #484848 !important;
  }

  &_menu-icon {
    margin: 15px;
    font-size: 18px;
    cursor: pointer;
  }

  &_column-menu {
    position: absolute;
    top: 25px;
    left: 3px;
  }

  &_column-slider {
    -webkit-appearance: none;
    height: 8px;
    border-radius: 2px;
    background: #e9e9e9;
  }

  &_menu {
    position: absolute;
    top: 36px;
    z-index: 9999;
    width: 280px;
    border: 1px solid #dddddd;
    border-top: 0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    background-image: linear-gradient(0deg, #f6f6f6, #ffffff);
    box-shadow: 0px 1px 2px 0px #888888;
  }

  &_menu-title {
    position: absolute;
    //margin-left: 6px;
    left: 40px;
    font-size: 14px;
    padding: 0 20px 20px 20px;
    height: 300px;
    width: 160px;
  }

  &_menu-button-area {
    margin-top: 15px;
    display: flex;
    gap: 18px;
    font-size: 20px;
  }

  &_menu-checkbox-area {
    height: 300px;
    overflow-y: auto;
    margin-top: 100px;
    padding-left: 30px;
  }

  &_no-data {
    position: absolute;
    top: 60px;
    left: 900px;
    font-size: 16px;
    color: #9d9d9d;
  }

}

</style>
